import {
  useDeleteFeePeriodMutation,
  FeePeriodsByAccountDocument,
} from '@transactions/graphql';
import * as React from 'react';
import {Button} from 'src/Button';
import {Modal} from 'src/Modal';
import type {FullFeePeriodFragment} from '@transactions/graphql';
import type {SystemProps} from 'src/Modals';

export type DeleteFeePeriodModalUserProps = {
  feePeriod: FullFeePeriodFragment;
  onDeleted: () => void;
};

export const DeleteFeePeriodModal = (
  props: DeleteFeePeriodModalUserProps & SystemProps,
) => {
  const {feePeriod, onRequestClose, onDeleted} = props;
  const [deleteFeePeriod, {loading}] = useDeleteFeePeriodMutation();
  const [error, setError] = React.useState<string | null>(null);

  const handleDelete = async () => {
    setError(null);
    try {
      await deleteFeePeriod({
        variables: {id: feePeriod._id},
        refetchQueries: [
          {
            query: FeePeriodsByAccountDocument,
            variables: {accountId: feePeriod.accountId},
          },
        ],
      });
      onDeleted();
      onRequestClose();
    } catch (e: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      const err = e as Error;
      setError(err.message);
    }
  };

  const footer = (
    <>
      <Button variant="outline" onClick={onRequestClose} disabled={loading}>
        Cancel
      </Button>
      <Button
        variant="destructive"
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onClick={handleDelete}
        disabled={loading || feePeriod.benefits.length > 0}
      >
        {loading ? 'Deleting...' : 'Delete'}
      </Button>
    </>
  );

  return (
    <Modal
      onRequestClose={onRequestClose}
      header="Delete fee period"
      footer={footer}
      size="sm"
    >
      {error && (
        <div className="bg-destructive/10 text-destructive mb-3 rounded-md p-3 text-sm">
          {error}
        </div>
      )}
      <p>
        Are you sure you want to delete this fee period? This action cannot be
        undone.
      </p>
      {feePeriod.benefits.length > 0 && (
        <p className="text-destructive mt-2 text-sm">
          This fee period has {feePeriod.benefits.length} benefit(s) linked.
          Delete all benefits first.
        </p>
      )}
    </Modal>
  );
};
